/**
 * @fileoverview Context for the modal
 *
 * @example
 * // Usage:
 * import { ModalContext } from './ModalContext'
 *
 * function Component() {
 *   // ...
 * }
 */

import { createContext } from 'react'


/**
 * @interface ModalConfig
 * @description The configuration for the modal
 * @property {() => JSX.Element} render - The function to render the modal
 * @property {boolean} canClose - Whether the modal can be closed
 * @property {() => Promise<unknown>} onCancel - The function to call when the modal is canceled
 * @property {() => Promise<unknown>} onConfirm - The function to call when the modal is confirmed
 * @property {() => Promise<unknown>} onClose - The function to call when the modal is closed
 */
export interface ModalConfig {
    render: (config: ModalConfig) => JSX.Element
    canClose?: boolean
    onCancel?: () => Promise<unknown>
    onConfirm?: () => Promise<unknown>
    onClose?: () => Promise<unknown>
}

/**
 * @interface ModalAPI
 * @description The API for the modal
 * @property {() => void} open - The function to open the modal
 * @property {() => void} close - The function to close the modal
 */
export interface ModalAPI {
    open: (config: ModalConfig) => void
    close: () => void
}

export const ModalContext = createContext<ModalAPI>(undefined)
